<template>
  <div>
    <div v-if="templates">
      <app-header
        :title="originalName ? 'Edit ' + originalName : 'New Template'"
        subtitle="Template"
      >
        <b-button
          variant="primary"
          class="mr-1"
          :disabled="!templates.svg"
          @click="save()"
        >
          Save Changes
        </b-button>
        <cancel-button />
      </app-header>
      <b-container>
        <b-spinner v-show="loading" />
        <b-row v-show="!loading">
          <b-col md="4">
            <b-form-group description="Apply settings based on filename">
              <b-button block variant="primary" @click="smartImport"
                >Smart Import</b-button
              >
            </b-form-group>

            <!-- Show any detected duplicate info -->
            <div v-if="matchDuplicates && matchDuplicates.length">
              <i class="text-warning fa fa-warning cursor-pointer" />
              Possible duplicate of
              <span
                v-for="(duplicate, index) in matchDuplicates"
                :key="`${index}-${duplicate}`"
              >
                <a
                  v-if="!isNaN(Number(duplicate))"
                  :href="`#/template/${duplicate}`"
                  target="_blank"
                >
                  {{ duplicate
                  }}<template v-if="index < matchDuplicates.length - 1"
                    >,
                  </template>
                </a>
                <template v-else
                  >{{ duplicate }}
                  <template v-if="index < matchDuplicates.length - 1"
                    >,</template
                  >
                </template>
              </span>
            </div>

            <label>Device</label>
            <i class="fa fa-asterisk" style="font-size:8px;color:red" />
            <app-device-selector v-model="templates.device_id" />

            <label>Type</label>
            <i class="fa fa-asterisk" style="font-size:8px;color:red" />
            <b-form-select
              v-model="templates.type_id"
              class="form-control"
              @input="onTypeDropdownChange"
            >
              <b-form-select-option
                v-for="type in templateTypes"
                :key="type.id"
                :value="type.id"
              >
                {{ type.name }}
              </b-form-select-option>
            </b-form-select>

            <!--Name-->
            <b-form-group>
              <label>Name </label>
              <i class="fa fa-asterisk" style="font-size:8px;color:red" />
              <b-form-input
                v-model="templates.name"
                type="text"
                required
              ></b-form-input>
            </b-form-group>
            <!--SVG-->
            <div class="form-group">
              <label>SVG</label>
              <i class="fa fa-asterisk" style="font-size:8px;color:red" />
              <FileInput
                v-model="templates.svg"
                required
                :folder="TEMPLATES_FOLDER"
              />
            </div>
            <div class="form-group" label="Categories (tick all that apply)">
              <label>Categories (tick all that apply)</label>
              <div class="form-check">
                <label class="form-check-label">
                  <input
                    v-model="templates.typenormal"
                    class="form-check-input"
                    type="checkbox"
                  />
                  Normal
                </label>
              </div>
              <div class="form-check">
                <label class="form-check-label">
                  <input
                    v-model="templates.typemagic"
                    class="form-check-input"
                    type="checkbox"
                  />
                  Magic
                </label>
              </div>
              <div class="form-check">
                <label class="form-check-label">
                  <input
                    v-model="templates.typeair"
                    class="form-check-input"
                    type="checkbox"
                  />
                  Air
                </label>
              </div>
              <div class="form-check">
                <label class="form-check-label">
                  <input
                    v-model="templates.typebody"
                    class="form-check-input"
                    type="checkbox"
                  />
                  Body
                </label>
              </div>
            </div>
            <div class="form-group">
              <label>Other Options</label>
              <div class="form-check">
                <label class="form-check-label">
                  <input
                    v-model="templates.easyinstall"
                    class="form-check-input"
                    type="checkbox"
                  />
                  Easy Install
                </label>
              </div>
              <div class="form-check">
                <label class="form-check-label">
                  <input
                    v-model="templates.cutonly"
                    class="form-check-input"
                    type="checkbox"
                  />
                  Cut Only
                </label>
              </div>
              <div class="form-check">
                <label class="form-check-label">
                  <input
                    v-model="templates.hidden"
                    class="form-check-input"
                    type="checkbox"
                  />
                  Hidden
                </label>
              </div>
            </div>
          </b-col>
          <b-col md="8">
            <app-smart-import-description />
          </b-col>
        </b-row>
      </b-container>
    </div>
    <input ref="fileInput" type="file" accept="*" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import $api from '@/services/api';
import $savefile from '@/services/savefile';
import AppHeader from '@/components/AppHeader';
import FileInput from '@/components/FileInput';
import CancelButton from '@/components/CancelButton';
import AppDeviceSelector from '@/components/devices/DeviceSelector';
import AppSmartImportDescription from '@/components/templates/SmartImportDescription';

const TEMPLATES_FOLDER = 'templates/svg';

export default {
  components: {
    AppHeader,
    FileInput,
    CancelButton,
    AppDeviceSelector,
    AppSmartImportDescription,
  },
  computed: {
    ...mapState('templatetypes', {
      templateTypes: 'list',
    }),
    ...mapState('devicetypes', {
      deviceTypes: 'list',
    }),
    ...mapState('manufacturers', {
      manufacturers: 'list',
    }),
    ...mapState('devices', {
      devices: 'list',
    }),
    ...mapGetters('devices', {
      deviceById: 'getDevice',
      filteredDevices: 'getTypeManufacturerDevices',
    }),
    ...mapState('smartimport', {
      smartValues: 'mapping',
    }),
    ...mapGetters('smartimport', {
      importDataLoading: 'loading',
      deviceIdByFilename: 'deviceIdByFilename',
      templateTypeByFilename: 'templateTypeByFilename',
      getPossibleDuplicatesDetails: 'getPossibleDuplicatesDetails',
    }),
    matchDuplicates() {
      if (!this.templates.device_id || !this.templates.type_id) {
        return null;
      }
      return this.getPossibleDuplicatesDetails(
        this.templates.id,
        this.templates.device_id,
        this.templates.type_id,
      );
    },
    loading() {
      return this.loadingData || this.importDataLoading;
    },
  },

  data() {
    return {
      originalName: null,
      templates: null,
      TEMPLATES_FOLDER,
      loadingData: false,
    };
  },

  created() {
    this.refreshData();
  },

  methods: {
    refreshData() {
      if (this.$route.query.id) {
        this.loadingData = true;
        $api
          .get(`/v1/internal/templates/${this.$route.query.id}`)
          .then((templates) => {
            this.originalName = templates.name;
            this.templates = templates;
            this.loadingData = false;
          });
      } else {
        this.templates = {
          device_id: this.$route.query.device_id,
          type_id: null,
          hidden: false,
        };
      }
    },

    save() {
      // Enforce typenormal as default if none selected
      if (
        !this.templates.typenormal &&
        !this.templates.typemagic &&
        !this.templates.typeair &&
        !this.templates.typebody
      ) {
        this.templates.typenormal = true;
      }
      if (this.templates.id) {
        $api
          .put(`/v1/internal/templates/${this.templates.id}`, this.templates)
          .then((templates) => {
            this.$store.dispatch('templates/fetch');
            this.$router.push({
              name: 'template-detail',
              params: templates,
            });
          });
      } else {
        $api
          .post('/v1/internal/templates', this.templates)
          .then((templates) => {
            this.$store.dispatch('templates/fetch');
            this.$router.push({
              name: 'template-detail',
              params: templates,
            });
          });
      }
    },

    onTypeDropdownChange(typeId) {
      const selectedName = this.templateTypes.find((type) => type.id === typeId)
        .name;
      this.templates.name = selectedName;
    },

    smartImport() {
      this.$refs.fileInput.click();
    },

    setDevice(filename) {
      const deviceId = this.deviceIdByFilename(filename);
      if (deviceId) {
        this.templates.device_id = deviceId;
      } else {
        alert('Could not find a device matching the filename');
      }
    },
    setType(filename) {
      const selectedType = this.templateTypeByFilename(filename);
      if (selectedType) {
        this.templates.type_id = selectedType.id;
        this.templates.name = selectedType.name;
      } else {
        alert('Could not find a type matching the filename');
      }
    },
    setCategories(categories) {
      this.templates.typenormal = categories.includes('NO');
      this.templates.typemagic = categories.includes('MA');
      this.templates.typeair = categories.includes('AI');
      this.templates.typebody = categories.includes('BO');
    },
    setOptions(options) {
      this.templates.easyinstall = options.includes('EI');
      this.templates.cutonly = options.includes('CO');
      this.templates.hidden = options.includes('H');
    },
    async uploadTemplateSVG(file) {
      this.loadingData = true;
      try {
        const svgUrl = await $savefile(file, this.TEMPLATES_FOLDER);
        this.templates.svg = svgUrl;
      } catch (e) {
        alert(`Error uploading SVG: ${e.message}`);
      }
      this.loadingData = false;
    },
  },
  mounted() {
    this.$store.dispatch('smartimport/ensure');

    const { fileInput } = this.$refs;

    // eslint-disable-next-line consistent-return
    fileInput.addEventListener('change', () => {
      if (fileInput.files.length > 0) {
        const file = fileInput.files[0];
        const filename = file.name.split('.svg')[0];
        const parts = filename.split('_');
        this.setDevice(file.name);
        this.setType(file.name);
        this.setCategories(parts[4]);
        this.setOptions(parts[5]);
        this.uploadTemplateSVG(file);
      }
    });
  },
};
</script>
<style scoped>
input[type='file'] {
  display: none;
}
.fa.fa-asterisk {
  transform: translateY(-5px);
  margin-left: 5px;
}
</style>
